import React, { useCallback, useContext, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { useControlledState } from 'ui-components/hooks/use-controlled-component-state/use-controlled-component-state';

import { createColumnDefinitionBuilder } from '../../../../../../../components/items-table/create-column-definition-builder';
import { ProductDetailsContext } from '../../../../../context';

import { messages } from '../../messages';
import { ProductTypeAheadInput } from '../../../../../../../components/product-type-ahead-input/product-type-ahead-input';
import style from '../../style.module.css';

const EditProductSkuColumnId = 'edit-sku';
const SkuPropertyName = 'sku';

const editProductSkuDataBuilder = (item) => {
  return {
    id: item.id,
    sku: item.sku,
    readonly: item.readonly,
    error: item.error,
    shouldShowError: item.shouldShowError,
  };
};

const EditProductSku = ({ value: item, onColumnBlur, disabled = false }) => {
  const context = useContext(ProductDetailsContext);
  const parentProduct = {
    name: context?.productName,
    sku: context?.productSku,
  };
  const [sku, setSku] = useControlledState(item.sku);

  const shouldShowErrorRef = useRef(false);
  const onBlur = useCallback(
    (event) => {
      shouldShowErrorRef.current = true;
      onColumnBlur(item.id, {
        [SkuPropertyName]: event?.target?.value,
      });
    },
    [onColumnBlur, item.id],
  );

  const onResultSelected = useCallback(
    (product) => {
      onColumnBlur(item.id, {
        name: product.name,
        sku: product.sku,
        productId: product.productId,
        productThumbnailUrl: product.thumbnailUrl,
      });
    },
    [onColumnBlur, item.id],
  );

  const onChange = useCallback(
    (value) => {
      setSku(value);
    },
    [setSku],
  );

  const error = useMemo(() => {
    return item.error ? <FormattedMessage {...item.error.message} /> : undefined;
  }, [item]);

  if (item.readonly) {
    return <div aria-labelledby={EditProductSkuColumnId}>{sku}</div>;
  }

  return (
    <ProductTypeAheadInput
      ariaLabelledBy={EditProductSkuColumnId}
      className={style.input}
      disabled={disabled}
      errorMessage={shouldShowErrorRef.current || item.shouldShowError ? error : undefined}
      excludeBundles
      onBlur={onBlur}
      onChange={onChange}
      onResultSelected={onResultSelected}
      parentProduct={parentProduct}
      value={sku}
    />
  );
};

EditProductSku.propTypes = {
  value: PropTypes.shape({
    id: PropTypes.string.isRequired,
    sku: PropTypes.string,
    readonly: PropTypes.shape({
      code: PropTypes.string,
    }),
    error: PropTypes.shape({
      message: PropTypes.object,
    }),
    shouldShowError: PropTypes.bool,
  }).isRequired,
  onColumnBlur: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

const createEditProductSkuColumnDefinition = createColumnDefinitionBuilder({
  header: <FormattedMessage {...messages.productBundleComponentSku} />,
  accessor: EditProductSkuColumnId,
  cell: EditProductSku,
  width: 1.5,
  className: style.column,
});

export { EditProductSkuColumnId, editProductSkuDataBuilder, createEditProductSkuColumnDefinition };
